import { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import {
  SearchIcon,
  UserGroupIcon,
  MailIcon,
  PhoneIcon,
  EyeIcon,
  OfficeBuildingIcon,
  CheckCircleIcon,
  XCircleIcon,
  RefreshIcon,
  ClockIcon,
} from "@heroicons/react/outline";
import { Dialog } from "@headlessui/react";
import ActionDropdown from "../../components/ActionDropdown";
import fire from "../../utils/Toastr";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";
import Paginate from "../../utils/Paginate";
import { formatDateForDisplay } from "../../utils/convertDate";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Initiated", label: "Initiated" },
  { value: "Success", label: "Success" },
  { value: "Failed", label: "Failed" },
];

const OFFICER_TYPE_OPTIONS = ["Maker", "Checker"];

const statusBadge = (status) => {
  const styles = {
    Initiated: "bg-yellow-100 text-yellow-800",
    Success: "bg-green-100 text-green-800",
    Failed: "bg-red-100 text-red-800",
  };
  return styles[status] || "bg-gray-100 text-gray-800";
};

export default function PreOnboardedPayrollOfficers() {
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (organizationId) {
      fetchPreOnboarded(1);
    }
  }, [organizationId]);

  const fetchOrganizations = async () => {
    try {
      let allOrgs = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await ThirdPartyServices.listOrganizations(page, 100);
        if (response.status && response.data?.result) {
          allOrgs = [...allOrgs, ...response.data.result];
          totalPages = response.data.totalPages || 1;
          page++;
        } else {
          break;
        }
      } while (page <= totalPages);

      setOrganizations(allOrgs);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const fetchPreOnboarded = async (page = 1) => {
    if (!organizationId) {
      fire("warning", "Please select an organization");
      return;
    }

    setLoading(true);
    try {
      const response = await ThirdPartyServices.listPreOnboardedPayrollOfficers({
        search: search || undefined,
        status: statusFilter || undefined,
        organizationId,
        page,
        pageSize: 10,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (response.status && response.data) {
        setOfficers(response.data.result || []);
        setPageIndex(response.data.pageIndex || page);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
        setHasNextPage(response.data.hasNextPage || false);
        setHasPreviousPage(response.data.hasPreviousPage || false);
      } else {
        setOfficers([]);
        fire("error", response.message || "Failed to fetch pre-onboarded officers");
      }
    } catch (err) {
      fire("error", "Failed to fetch pre-onboarded officers");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPreOnboarded(1);
  };

  const handlePageChange = (page) => {
    setPageIndex(page);
    fetchPreOnboarded(page);
  };

  const handleViewDetails = async (officer) => {
    setSelectedOfficer(officer);
    setDetailLoading(true);
    setShowDetailModal(true);

    try {
      const response = await ThirdPartyServices.viewPreOnboardedPayrollOfficer(
        officer.organizationId,
        officer.id,
      );
      if (response.status && response.data) {
        setDetailData(response.data);
      } else {
        setDetailData(officer);
      }
    } catch (err) {
      setDetailData(officer);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedOfficer || !reason.trim()) {
      fire("warning", "Please provide a reason");
      return;
    }

    setActionLoading(true);
    try {
      const response = await ThirdPartyServices.approveOrRejectPreOnboarded({
        tenantId: selectedOfficer.tenantId,
        organizationId: selectedOfficer.organizationId,
        preOnboardedPayrollOfficerId: selectedOfficer.id,
        status: "Success",
        reason: reason,
      });

      if (response.status) {
        fire("success", "Pre-onboarded officer approved successfully");
        setShowApproveModal(false);
        setReason("");
        setSelectedOfficer(null);
        fetchPreOnboarded(pageIndex);
      } else {
        fire("error", response.message || "Approval failed");
      }
    } catch (err) {
      fire("error", "Approval failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedOfficer || !reason.trim()) {
      fire("warning", "Please provide a reason");
      return;
    }

    setActionLoading(true);
    try {
      const response = await ThirdPartyServices.approveOrRejectPreOnboarded({
        tenantId: selectedOfficer.tenantId,
        organizationId: selectedOfficer.organizationId,
        preOnboardedPayrollOfficerId: selectedOfficer.id,
        status: "Failed",
        reason: reason,
      });

      if (response.status) {
        fire("success", "Pre-onboarded officer rejected");
        setShowRejectModal(false);
        setReason("");
        setSelectedOfficer(null);
        fetchPreOnboarded(pageIndex);
      } else {
        fire("error", response.message || "Rejection failed");
      }
    } catch (err) {
      fire("error", "Rejection failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendWebhook = async (officer) => {
    setSelectedOfficer(officer);
    setShowWebhookModal(true);
  };

  const confirmSendWebhook = async () => {
    if (!selectedOfficer) return;

    setActionLoading(true);
    try {
      const org = organizations.find(
        (o) => o.organizationId === selectedOfficer.organizationId,
      );
      const response = await ThirdPartyServices.sendWebhookNotification({
        preOnboardedPayrollOfficerId: selectedOfficer.id,
        organizationId: selectedOfficer.organizationId,
        applicationName: org?.applicationName || "",
      });

      if (response.status) {
        fire("success", "Webhook notification sent successfully");
        setShowWebhookModal(false);
        setSelectedOfficer(null);
      } else {
        fire("error", response.message || "Failed to send webhook notification");
      }
    } catch (err) {
      fire("error", "Failed to send webhook notification");
    } finally {
      setActionLoading(false);
    }
  };

  const ActionModal = ({ open, onClose, onConfirm, title, confirmLabel, confirmColor }) => (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white">
          <div className="px-6 py-4 border-b border-gray-200">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              {title}
            </Dialog.Title>
          </div>
          <div className="px-6 py-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
              placeholder="Enter reason for this action..."
            />
          </div>
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={actionLoading || !reason.trim()}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${confirmColor} disabled:opacity-50`}
            >
              {actionLoading ? (
                <HollowDotsSpinner size={20} color="#fff" />
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );

  return (
    <Layout current="thirdparty" pageTag="thirdparty">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Pre-Onboarded Payroll Officers
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                Review and approve/reject pre-onboarded payroll officer requests
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization *
                </label>
                <select
                  value={organizationId}
                  onChange={(e) => {
                    setOrganizationId(e.target.value);
                    setSearch("");
                    setPageIndex(1);
                  }}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option
                      key={org.organizationId}
                      value={org.organizationId}
                    >
                      {org.companyName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPageIndex(1);
                  }}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-full border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="mt-4 flex items-center space-x-3">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`pl-10 block w-full border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                    !organizationId ? "bg-gray-100" : ""
                  }`}
                  placeholder={
                    organizationId
                      ? "Search by email, phone or reason..."
                      : "Select organization first"
                  }
                  disabled={!organizationId}
                />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                disabled={!organizationId}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  organizationId
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-gray-400 cursor-not-allowed"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
              >
                <SearchIcon className="h-4 w-4 mr-1.5" />
                Search
              </button>
              {organizationId && (
                <button
                  type="button"
                  onClick={() => fetchPreOnboarded(1)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RefreshIcon className="h-4 w-4 mr-1.5" />
                  Refresh
                </button>
              )}
            </div>
          </div>

          {!organizationId ? (
            <div className="text-center py-12">
              <OfficeBuildingIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Select an Organization
              </h3>
              <p className="text-gray-500">
                Please select an organization to view pre-onboarded payroll
                officers.
              </p>
            </div>
          ) : loading ? (
            <div className="flex justify-center items-center h-64">
              <HollowDotsSpinner color="#4A90E2" />
            </div>
          ) : officers.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No pre-onboarded officers found
              </h3>
              <p className="text-gray-500">
                {search || statusFilter
                  ? "Try adjusting your search or filter criteria."
                  : "No pre-onboarded payroll officers for the selected organization."}
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          BVN
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reason
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {officers.map((officer, i) => (
                        <tr
                          key={officer.id}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-900">
                                <MailIcon className="h-4 w-4 mr-2 text-gray-400" />
                                {officer.emailAddress}
                              </div>
                              <div className="flex items-center text-sm text-gray-500">
                                <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                                {officer.phoneNumber}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-sm font-mono text-gray-900">
                                {officer.bvn}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                officer.officerType === "Maker"
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-orange-100 text-orange-800"
                              }`}
                            >
                              {officer.officerType}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge(officer.status)}`}
                            >
                              {officer.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 max-w-[200px]">
                            <p className="text-sm text-gray-500 truncate">
                              {officer.reason || "N/A"}
                            </p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <ActionDropdown
                              dropdownWidth="w-56"
                              items={[
                                { label: "View Details", onClick: () => handleViewDetails(officer), icon: EyeIcon },
                                { label: "Approve", onClick: () => { setSelectedOfficer(officer); setShowApproveModal(true); }, icon: CheckCircleIcon, className: "text-green-600" },
                                { label: "Reject", onClick: () => { setSelectedOfficer(officer); setShowRejectModal(true); }, icon: XCircleIcon, className: "text-red-600" },
                                { label: "Send Webhook", onClick: () => handleSendWebhook(officer), icon: MailIcon },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="mt-6">
                  <Paginate
                    nextPage={hasNextPage}
                    pageIndex={pageIndex}
                    totalCount={totalCount}
                    prevPage={hasPreviousPage}
                    totalPages={totalPages}
                    setPageIndex={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Approve Modal */}
      <ActionModal
        open={showApproveModal}
        onClose={() => {
          setShowApproveModal(false);
          setReason("");
        }}
        onConfirm={handleApprove}
        title="Approve Pre-Onboarded Officer"
        confirmLabel="Approve"
        confirmColor="bg-green-600 hover:bg-green-700"
      />

      {/* Reject Modal */}
      <ActionModal
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setReason("");
        }}
        onConfirm={handleReject}
        title="Reject Pre-Onboarded Officer"
        confirmLabel="Reject"
        confirmColor="bg-red-600 hover:bg-red-700"
      />

      {/* Webhook Confirm Modal */}
      <Dialog
        open={showWebhookModal}
        onClose={() => {
          setShowWebhookModal(false);
          setSelectedOfficer(null);
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm w-full rounded-lg bg-white">
            <div className="px-6 py-4 border-b border-gray-200">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                Send Webhook Notification
              </Dialog.Title>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600">
                Send a webhook notification for this pre-onboarded payroll
                officer? This will notify the third-party application about the
                onboarding status.
              </p>
              {selectedOfficer && (
                <div className="mt-4 bg-gray-50 rounded-md p-3">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Email:</span>{" "}
                    {selectedOfficer.emailAddress}
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="font-medium">Status:</span>{" "}
                    {selectedOfficer.status}
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowWebhookModal(false);
                  setSelectedOfficer(null);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSendWebhook}
                disabled={actionLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? (
                  <HollowDotsSpinner size={20} color="#fff" />
                ) : (
                  "Send Notification"
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Detail View Modal */}
      <Dialog
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setDetailData(null);
          setSelectedOfficer(null);
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-2xl w-full rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <Dialog.Title className="text-lg font-medium text-gray-900">
                  Pre-Onboarded Officer Details
                </Dialog.Title>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setDetailData(null);
                    setSelectedOfficer(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {detailLoading ? (
              <div className="flex justify-center items-center h-48">
                <HollowDotsSpinner color="#4A90E2" />
              </div>
            ) : detailData ? (
              <div className="px-6 py-4 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-3">
                      Officer Information
                    </h4>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-xs text-gray-400">Email</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.emailAddress}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">Phone</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.phoneNumber}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">BVN</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.bvn}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">Officer Type</dt>
                        <dd className="text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              detailData.officerType === "Maker"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            {detailData.officerType}
                          </span>
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-3">
                      Status & Organization
                    </h4>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-xs text-gray-400">Status</dt>
                        <dd>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge(detailData.status)}`}
                          >
                            {detailData.status}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">Reason</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.reason || "N/A"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {detailData.bvnDetails && (
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-sm font-medium text-gray-500 mb-3">
                      BVN Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-xs text-gray-400">Name on Card</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.bvnDetails.nameOnCard}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">Gender</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.bvnDetails.gender}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">Email</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.bvnDetails.email}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">Phone Number</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.bvnDetails.phoneNumber1}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">State of Origin</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.bvnDetails.stateOfOrigin}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">State of Residence</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.bvnDetails.stateOfResidence}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">Residential Address</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.bvnDetails.residentialAddress}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-400">Marital Status</dt>
                        <dd className="text-sm text-gray-900">
                          {detailData.bvnDetails.maritalStatus}
                        </dd>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                No details available
              </div>
            )}

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowDetailModal(false);
                  setDetailData(null);
                  setSelectedOfficer(null);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Layout>
  );
}
